// src/components/PlayerProfileModal.js
// Full cross-season stats modal for a claimed player. Opened from Profile.js.
// Fetches its own seasons + player data — does NOT rely on SeasonContext (Profile page has no leagueID in URL).

import React, { useEffect, useRef, useState, useMemo } from "react";
import { getDoc, getDocs, doc, collection } from "firebase/firestore";
import { db } from "../lib/firebase";
import { HeadingMedium, BodyText } from "../central/Typography";

// ─── Inline bar chart (reused for points and price) ───────────────────────────
function BarChart({ rows, labelKey, valueKey, valueSuffix = "", showAvgLine = false }) {
  if (!rows || rows.length === 0) {
    return (
      <BodyText style={{ margin: "0.6rem 0 0", fontSize: "0.8rem", color: "#666" }}>
        No data yet
      </BodyText>
    );
  }

  const values = rows.map((r) => Number(r[valueKey] || 0));
  const maxValue = Math.max(...values, 1);
  const avg = values.reduce((s, v) => s + v, 0) / (values.length || 1);
  const avgHeight = maxValue ? (avg / maxValue) * 92 : 0;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "flex-end",
        gap: "0.45rem",
        height: "140px",
        marginTop: "0.75rem",
        overflowX: "auto",
        paddingBottom: "0.2rem",
      }}
    >
      {showAvgLine && (
        <>
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: `${Math.max(avgHeight, 6)}px`,
              borderTop: "2px dotted #999",
              opacity: 0.8,
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 0,
              bottom: `${Math.max(avgHeight, 6) + 4}px`,
              fontSize: "0.68rem",
              color: "#666",
              fontStyle: "italic",
              background: "rgba(255,255,255,0.9)",
              padding: "0 0.2rem",
              lineHeight: 1,
            }}
          >
            {avg.toFixed(1)} Avg
          </div>
        </>
      )}
      {rows.map((row, i) => {
        const v = Number(row[valueKey] || 0);
        const barHeight = Math.max((v / maxValue) * 92, v > 0 ? 12 : 6);
        return (
          <div
            key={i}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
              minWidth: "34px",
              flex: "0 0 auto",
              height: "100%",
            }}
          >
            <BodyText
              style={{
                margin: "0 0 0.25rem",
                fontSize: "0.72rem",
                fontWeight: "700",
                color: "#234",
                lineHeight: 1,
              }}
            >
              {typeof row[valueKey] === "number" && valueSuffix
                ? `${v.toFixed(valueSuffix === "m" ? 1 : 0)}${valueSuffix}`
                : v}
            </BodyText>
            <div
              style={{
                width: "24px",
                height: `${barHeight}px`,
                borderRadius: "8px 8px 4px 4px",
                background: "#50B4C8",
              }}
            />
            <BodyText
              style={{
                margin: "0.35rem 0 0",
                fontSize: "0.7rem",
                color: "#666",
                lineHeight: 1,
                textAlign: "center",
              }}
            >
              {row[labelKey]}
            </BodyText>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────
export default function PlayerProfileModal({ open, onClose, leagueID, playerID, player }) {
  // "All" or a specific season ID
  const [selectedView, setSelectedView] = useState("All");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  // { [seasonID]: playerDoc | null }
  const [allSeasonData, setAllSeasonData] = useState({});
  // { [seasonID]: playerDoc[] } — for rank computation
  const [allPlayersData, setAllPlayersData] = useState({});
  const [loading, setLoading] = useState(false);

  // Drag-to-dismiss (mirrors PlayerCardModal)
  const sheetRef = useRef(null);
  const touchStartYRef = useRef(null);
  const touchCurrentYRef = useRef(null);
  const dragEnabledRef = useRef(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isDraggingSheet, setIsDraggingSheet] = useState(false);

  const handleTouchStart = (e) => {
    const startY = e.touches?.[0]?.clientY ?? null;
    touchStartYRef.current = startY;
    touchCurrentYRef.current = startY;
    dragEnabledRef.current = !sheetRef.current || sheetRef.current.scrollTop <= 0;
  };
  const handleTouchMove = (e) => {
    if (!dragEnabledRef.current) return;
    const currentY = e.touches?.[0]?.clientY ?? null;
    touchCurrentYRef.current = currentY;
    const startY = touchStartYRef.current;
    if (typeof startY !== "number" || typeof currentY !== "number") return;
    const deltaY = currentY - startY;
    if (deltaY <= 0) { setDragOffsetY(0); return; }
    if (typeof e.preventDefault === "function") e.preventDefault();
    setIsDraggingSheet(true);
    setDragOffsetY(deltaY);
  };
  const handleTouchEnd = () => {
    const delta = typeof touchStartYRef.current === "number" && typeof touchCurrentYRef.current === "number"
      ? touchCurrentYRef.current - touchStartYRef.current : 0;
    const shouldClose = dragEnabledRef.current && delta > 110;
    touchStartYRef.current = null;
    touchCurrentYRef.current = null;
    dragEnabledRef.current = false;
    setIsDraggingSheet(false);
    setDragOffsetY(0);
    if (shouldClose) onClose?.();
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setSelectedView("All");
      setDropdownOpen(false);
    }
  }, [open]);

  // Fetch seasons + all-season player docs on open
  useEffect(() => {
    if (!open || !leagueID || !playerID) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const seasonsSnap = await getDocs(collection(db, "leagues", leagueID, "seasons"));
        const seasons = seasonsSnap.docs
          .map((d) => d.id)
          .filter((id) => /^S\d+$/.test(id))
          .sort((a, b) => parseInt(a.replace("S", ""), 10) - parseInt(b.replace("S", ""), 10));

        if (cancelled) return;
        setAvailableSeasons(seasons);

        // If only one season, jump straight to it
        if (seasons.length === 1) setSelectedView(seasons[0]);

        // Fetch this player's doc from every season in parallel
        const results = await Promise.all(
          seasons.map(async (s) => {
            try {
              const snap = await getDoc(doc(db, "leagues", leagueID, "seasons", s, "players", playerID));
              return [s, snap.exists() ? { id: snap.id, ...snap.data() } : null];
            } catch {
              return [s, null];
            }
          })
        );
        if (!cancelled) setAllSeasonData(Object.fromEntries(results));
      } catch (e) {
        console.error("[PlayerProfileModal] fetch failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, leagueID, playerID]);

  // Fetch all players for the selected season when needed for ranking
  useEffect(() => {
    if (!open || !leagueID || selectedView === "All") return;
    if (allPlayersData[selectedView] !== undefined) return; // already fetched
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "leagues", leagueID, "seasons", selectedView, "players"));
        if (!cancelled) {
          setAllPlayersData((prev) => ({
            ...prev,
            [selectedView]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          }));
        }
      } catch {
        if (!cancelled) setAllPlayersData((prev) => ({ ...prev, [selectedView]: [] }));
      }
    })();
    return () => { cancelled = true; };
  }, [open, leagueID, selectedView]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Computed data ──────────────────────────────────────────────────────────
  const aggregated = useMemo(() => {
    const seasons = Object.keys(allSeasonData).filter((s) => allSeasonData[s]);
    const sum = (key) => seasons.reduce((acc, s) => acc + Number(allSeasonData[s]?.stats?.[key] || 0), 0);
    return {
      totalPoints: sum("totalPoints"),
      appearances: sum("appearances"),
      goals: sum("goals"),
      assists: sum("assists"),
      cleanSheets: sum("cleanSheets"),
      saves: sum("saves"),
      bonus: sum("bonus"),
      yellowCards: sum("yellowCards"),
    };
  }, [allSeasonData]);

  const seasonDoc = selectedView !== "All" ? (allSeasonData[selectedView] || null) : null;
  const seasonStats = seasonDoc?.stats || {};

  const displayStats = selectedView === "All" ? aggregated : {
    totalPoints: Number(seasonStats.totalPoints || 0),
    appearances: Number(seasonStats.appearances || 0),
    goals: Number(seasonStats.goals || 0),
    assists: Number(seasonStats.assists || 0),
    cleanSheets: Number(seasonStats.cleanSheets || 0),
    saves: Number(seasonStats.saves || 0),
    bonus: Number(seasonStats.bonus || 0),
    yellowCards: Number(seasonStats.yellowCards || 0),
  };

  const ptsPerApp = displayStats.appearances > 0
    ? Number((displayStats.totalPoints / displayStats.appearances).toFixed(1))
    : 0;

  // Rank helper — only meaningful when a specific season is selected
  const getRank = (statKey) => {
    if (selectedView === "All") return null;
    const pool = allPlayersData[selectedView];
    if (!pool || !pool.length) return null;
    const sorted = pool
      .map((p) => ({ id: p.id, value: Number(p?.stats?.[statKey] || 0) }))
      .sort((a, b) => b.value - a.value);
    let rank = 1;
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i].value < sorted[i - 1].value) rank = i + 1;
      if (sorted[i].id === playerID) return rank;
    }
    return null;
  };

  // Points chart data
  const pointsChartRows = useMemo(() => {
    if (selectedView === "All") {
      return availableSeasons.map((s) => ({
        label: s,
        value: Number(allSeasonData[s]?.stats?.totalPoints || 0),
      }));
    }
    // GW-by-GW for selected season
    const history = seasonDoc?.pointsHistory || {};
    return Object.keys(history)
      .sort((a, b) => {
        const an = Number(String(a).replace("GW", "")) || 0;
        const bn = Number(String(b).replace("GW", "")) || 0;
        return an - bn;
      })
      .map((gw) => ({ label: gw, value: Number(history[gw] || 0) }));
  }, [selectedView, availableSeasons, allSeasonData, seasonDoc]);

  // Price chart data
  const priceChartRows = useMemo(() => {
    if (selectedView === "All") {
      return availableSeasons.map((s) => {
        const sDoc = allSeasonData[s];
        if (!sDoc) return { label: s, value: 0 };
        // Use final priceHistory entry if available, else currentPrice
        const ph = sDoc.priceHistory || {};
        const gwKeys = Object.keys(ph).sort((a, b) => {
          const an = Number(String(a).replace("GW", "")) || 0;
          const bn = Number(String(b).replace("GW", "")) || 0;
          return an - bn;
        });
        const finalPrice = gwKeys.length > 0
          ? Number(ph[gwKeys[gwKeys.length - 1]] || 0)
          : Number(sDoc.currentPrice || 0);
        return { label: s, value: finalPrice };
      });
    }
    const ph = seasonDoc?.priceHistory || {};
    return Object.keys(ph)
      .sort((a, b) => {
        const an = Number(String(a).replace("GW", "")) || 0;
        const bn = Number(String(b).replace("GW", "")) || 0;
        return an - bn;
      })
      .map((gw) => ({ label: gw, value: Number(ph[gw] || 0) }));
  }, [selectedView, availableSeasons, allSeasonData, seasonDoc]);

  // ── Stat rows config ───────────────────────────────────────────────────────
  const statRows = [
    { label: "Goals", key: "goals" },
    { label: "Assists", key: "assists" },
    { label: "Clean Sheets", key: "cleanSheets" },
    { label: "Saves", key: "saves" },
    { label: "Bonus", key: "bonus" },
    { label: "Yellow Cards", key: "yellowCards" },
  ];

  const playerName = player?.name || "Player";
  const playerPosition = player?.position || "";

  const showSeasonPill = availableSeasons.length > 1;
  const currentViewLabel = selectedView;

  if (!open || !player) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overscrollBehavior: "contain",
      }}
    >
      <div
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          width: "100%",
          maxWidth: "560px",
          maxHeight: "88vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          background: "#fff",
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "1rem 1rem 1.5rem",
          boxSizing: "border-box",
          boxShadow: "0 -10px 30px rgba(0,0,0,0.2)",
          transform: `translateY(${dragOffsetY}px)`,
          transition: isDraggingSheet ? "none" : "transform 180ms ease-out",
          WebkitOverflowScrolling: "touch",
          willChange: "transform",
        }}
      >
        {/* Drag handle */}
        <div
          style={{
            width: "44px",
            height: "5px",
            borderRadius: "999px",
            background: "#d1d5db",
            margin: "0 auto 0.85rem",
          }}
        />

        {/* Top row: season pill + Done */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "0.5rem",
            marginBottom: "0.75rem",
          }}
        >
          {showSeasonPill && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                style={{
                  border: "1px solid #d9e0e6",
                  borderRadius: "999px",
                  padding: "0.18rem 0.55rem",
                  background: "#f4f7f8",
                  fontSize: "0.75rem",
                  fontWeight: "600",
                  color: "#555",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  lineHeight: 1.4,
                }}
              >
                {currentViewLabel} ▾
              </button>
              {dropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 4px)",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    padding: "0.35rem",
                    zIndex: 10,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    minWidth: "80px",
                  }}
                >
                  {["All", ...availableSeasons].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setSelectedView(s);
                        setDropdownOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        background: s === selectedView ? "#50B4C8" : "transparent",
                        color: s === selectedView ? "#fff" : "#234",
                        border: "none",
                        borderRadius: "8px",
                        padding: "0.3rem 0.6rem",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        cursor: "pointer",
                        textAlign: "center",
                        fontFamily: "inherit",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: "0.95rem",
              fontWeight: "600",
              color: "#555",
              cursor: "pointer",
              padding: 0,
              lineHeight: 1,
            }}
          >
            Done
          </button>
        </div>

        {/* Player name + position */}
        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          <HeadingMedium style={{ margin: 0, fontSize: "1.3rem" }}>
            {playerName}
          </HeadingMedium>
          {playerPosition && (
            <div
              style={{
                display: "inline-block",
                marginTop: "0.35rem",
                background: "#50B4C8",
                color: "#fff",
                borderRadius: "999px",
                padding: "0.18rem 0.65rem",
                fontSize: "0.78rem",
                fontWeight: "700",
              }}
            >
              {playerPosition}
            </div>
          )}
          {selectedView !== "All" && (
            <BodyText style={{ margin: "0.3rem 0 0", fontSize: "0.75rem", color: "#999" }}>
              Viewing {selectedView} data
            </BodyText>
          )}
        </div>

        {loading ? (
          <BodyText style={{ textAlign: "center", color: "#999", padding: "2rem 0" }}>
            Loading…
          </BodyText>
        ) : (
          <>
            {/* ── Top 3 stat cards ─────────────────────────────────── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "0.5rem",
                marginBottom: "0.9rem",
              }}
            >
              {[
                { label: "Total Points", value: displayStats.totalPoints },
                { label: "Appearances", value: displayStats.appearances },
                { label: "Pts / App", value: ptsPerApp },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    background: "#f4f7f8",
                    borderRadius: "14px",
                    border: "1px solid #e5e7eb",
                    padding: "0.65rem 0.7rem",
                  }}
                >
                  <BodyText style={{ margin: 0, fontSize: "0.74rem", color: "#666" }}>
                    {label}
                  </BodyText>
                  <BodyText
                    style={{ margin: "0.15rem 0 0", fontSize: "1rem", fontWeight: "700", color: "#111" }}
                  >
                    {value}
                  </BodyText>
                </div>
              ))}
            </div>

            {/* ── Stat rows ─────────────────────────────────────────── */}
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "16px",
                overflow: "hidden",
                marginBottom: "0.9rem",
              }}
            >
              {statRows.map((row, index) => {
                const value = displayStats[row.key];
                const rank = selectedView !== "All" ? getRank(row.key) : null;
                return (
                  <div
                    key={row.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "0.78rem 0.9rem",
                      background: index % 2 === 0 ? "#fff" : "#fafbfc",
                      borderTop: index === 0 ? "none" : "1px solid #eef2f4",
                    }}
                  >
                    <BodyText
                      style={{ margin: 0, fontSize: "0.88rem", color: "#234", fontWeight: "600" }}
                    >
                      {row.label}
                    </BodyText>
                    <BodyText
                      style={{ margin: 0, fontSize: "0.92rem", color: "#111", fontWeight: "700" }}
                    >
                      {value}
                      {rank !== null && (
                        <span style={{ fontStyle: "italic", color: "#666" }}>
                          {` • #${rank}`}
                        </span>
                      )}
                    </BodyText>
                  </div>
                );
              })}
            </div>

            {/* ── Points chart ─────────────────────────────────────── */}
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                padding: "0.75rem 0.8rem",
                marginBottom: "0.9rem",
                background: "#fff",
              }}
            >
              <BodyText
                style={{ margin: 0, fontSize: "0.8rem", fontWeight: "700", color: "#111" }}
              >
                {selectedView === "All" ? "Points by Season" : "Points by GW"}
              </BodyText>
              <BarChart
                rows={pointsChartRows}
                labelKey="label"
                valueKey="value"
                showAvgLine={true}
              />
            </div>

            {/* ── Price chart ──────────────────────────────────────── */}
            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: "14px",
                padding: "0.75rem 0.8rem",
                marginBottom: "0.5rem",
                background: "#fff",
              }}
            >
              <BodyText
                style={{ margin: 0, fontSize: "0.8rem", fontWeight: "700", color: "#111" }}
              >
                {selectedView === "All" ? "Price by Season" : "Price by GW"}
              </BodyText>
              <BarChart
                rows={priceChartRows}
                labelKey="label"
                valueKey="value"
                valueSuffix="m"
                showAvgLine={false}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
